import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import type { Weekday } from '@lensmira/domain'
import type { MessageKey } from '@lensmira/i18n'

import { useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { DataSurface } from './DataSurface.js'
import { DAY_MINUTES, minuteOf, timeOf } from '../minutes.js'

/**
 * A branch's base weekly appointment hours and slot rules. §6.14 `APT-001`, `APT-002`, Appendix J;
 * `decisions/0034`.
 *
 * **This is the row the other seven appointment screens stand on.** `isWorkingDay` reads
 * `appointment_schedule` before any closure or seasonal override, and `loadBranchSchedule` refuses
 * a branch without one:
 *
 * > This branch has no appointment hours or slot rules set, so nothing can be booked.
 *
 * `appointment_schedule` was in the detector's third pass with `appointment` and
 * `appointment_resource_booking`: an API that can set it and no screen that reaches the path. So a
 * new shop's diary answered `APPOINTMENT_SCHEDULE_MISSING` for ever, no slot could be offered, and
 * the seasonal-hours screen edited exceptions to hours that did not exist.
 *
 * **Nothing here has a default.** `schedule.service.ts` and `branch-schedule.ts` both say why: "a
 * branch with no `appointment_schedule` row has had no hours chosen, and inventing nine to six
 * would be a product decision made by a developer". An unset branch gets empty fields and a
 * sentence asking, and a save cannot go with a field left blank.
 *
 * **`null` from the `GET` is not an error.** It is the ordinary state of a new shop, so the surface
 * stays `ready` and the screen says so in words above an empty form. `DataSurface`'s `empty` and
 * `filtered` states are unreachable here and their copy is not passed: this is one record rather
 * than a list, there is nothing to filter, and showing `lm-empty` instead of the form would be a
 * dead end on the one screen whose whole job is to fill it in.
 *
 * **Every refusal the server can give is checked here first.** `transport.ts` treats 422 and 409 as
 * refusals and a blocked head halts the whole queue, taking payment included. Worse, a check
 * constraint reaches the counter as a **500**, which the queue reads as unreachable and retries for
 * ever with everything behind it (`requireId`'s comment in `write/idempotency.ts` records the same
 * shape). Two of the checks below exist for that second case and not the first.
 *
 * **No day is ticked until somebody ticks it, and the days are in the column's own order.** The
 * column is `smallint().array()` with the comment "Pakistan's week is not everyone's, so no default
 * weekend", so the screen pre-ticks nothing and claims no weekend. It also does not reorder the
 * week: which day a week starts on belongs to §13's country pack, which is not built
 * (`countryHolidays` is `[]` everywhere), so the list runs 0 to 6 as the column numbers it and the
 * copy says that is a numbering and not a claim.
 *
 * **Zero days is refused here**, which the service's own header says it is not: "an empty list is a
 * branch that opens on no day ... refused only by the slot arithmetic finding nothing". It is
 * refused three harder ways than that, and none of them is a sentence a shopkeeper gets to read.
 * `appointment_schedule_weekdays` is `cardinality(open_weekdays) >= 1`, the path does not go
 * through `refusing()`, so the violation is a 500 and the queue retries it for ever; and
 * `validateCalendar` throws `NO_OPEN_WEEKDAY`, which makes the diary's slot read 422 and makes the
 * outbox hold that branch's messages silently (`isSendableNow` catches `AppointmentError` and
 * returns false).
 *
 * **Closing at midnight.** The wire is minutes from midnight and `closesAtMinute` accepts 1440,
 * which `<input type="time">` cannot express. The convention is `AppointmentCalendar`'s, not a
 * second one: the form offers 23:59, a closing time of 00:00 is refused with the sentence that says
 * what to type instead, and a stored 1440 is kept until the closing time is changed. That last part
 * is not cosmetic: `availableSlots` offers a start only while `start + duration <= closesAtMinute`,
 * so rewriting 1440 as 1439 silently loses the last slot of the day.
 *
 * **Owner alone** (`tenant_settings_policies`, both routes). `OPEN.md` §4.2 records the cost: a
 * branch manager cannot set their own branch's hours. Without the row no read is made and the whole
 * panel is the denied state.
 */

/** `GET /branches/:branchId/appointment-schedule`, or `null` where none is set. */
export interface ScheduleRow {
  readonly branchId: string
  /** 0 for Sunday. A list and not a bitmask, which is what the column is. */
  readonly openWeekdays: readonly number[]
  readonly opensAtMinute: number
  readonly closesAtMinute: number
  readonly slotStepMinutes: number
  readonly bufferMinutes: number
  readonly minNoticeMinutes: number
  readonly maxAdvanceDays: number
  readonly version: number
}

export interface AppointmentHoursProps {
  /** `tenant_settings_policies`, the owner alone. Without it nothing is read and nothing shown. */
  readonly canManage: boolean
  readonly branchId: string
  readonly branchName: string
  /**
   * The branch's hours, or `null` where none has been set.
   *
   * **Null means "no row", never "could not read".** A loader that answers null on a failed read
   * would put this screen into the unset state, and the save that followed would omit
   * `expectedVersion` against a row that has one: `EXPECTED_VERSION_REQUIRED`, at the head of the
   * queue, which stops the till. A failed read has to throw, and reaches the `error` state with a
   * retry. Must be referentially stable (`useCallback`) or the screen reads on every render.
   */
  readonly loadSchedule: (branchId: string) => Promise<ScheduleRow | null>
  readonly onWritten?: () => void
}

/**
 * The seven days, in the order the column numbers them.
 *
 * Each key is a literal rather than a template, because `i18n-static.test.ts` scans for key
 * literals: a `` `apptHours.weekday.${day}` `` would read as seven keys nothing uses and the
 * catalogue entries would be deleted as dead.
 */
const WEEKDAYS: readonly { readonly day: Weekday; readonly key: MessageKey }[] = [
  { day: 0, key: 'apptHours.weekday.sunday' },
  { day: 1, key: 'apptHours.weekday.monday' },
  { day: 2, key: 'apptHours.weekday.tuesday' },
  { day: 3, key: 'apptHours.weekday.wednesday' },
  { day: 4, key: 'apptHours.weekday.thursday' },
  { day: 5, key: 'apptHours.weekday.friday' },
  { day: 6, key: 'apptHours.weekday.saturday' },
]

type RuleKey = 'slotStep' | 'buffer' | 'notice' | 'advance'

/**
 * The four numbers, their labels and the bounds a save is held to.
 *
 * The first three bounds are `schedule.service.ts`'s own, exactly: a step of 1 to 1440 minutes, a
 * buffer of 0 to 1440, a notice of 0 to 365 days of minutes. Each would be a 422 and a blocked
 * queue.
 *
 * **`advance`'s ceiling is this screen's**, and that is worth saying plainly. The service checks
 * only `maxAdvanceDays >= 1` and the column only `>= 0`, so the next bound up is `integer`, and a
 * number past it is SQLSTATE 22003, a 500, which the queue retries for ever rather than refusing.
 * 3,650 days is ten years, the same ceiling `RecallRules` chose for a span of days, and no shop
 * books further ahead than its own lease.
 */
const RULES: readonly {
  readonly key: RuleKey
  readonly labelKey: MessageKey
  readonly hintKey: MessageKey
  readonly min: number
  readonly max: number
}[] = [
  {
    key: 'slotStep',
    labelKey: 'apptHours.slotStep',
    hintKey: 'apptHours.slotStepMeans',
    min: 1,
    max: DAY_MINUTES,
  },
  {
    key: 'buffer',
    labelKey: 'apptHours.buffer',
    hintKey: 'apptHours.bufferMeans',
    min: 0,
    max: DAY_MINUTES,
  },
  {
    key: 'notice',
    labelKey: 'apptHours.notice',
    hintKey: 'apptHours.noticeMeans',
    min: 0,
    max: 365 * DAY_MINUTES,
  },
  {
    key: 'advance',
    labelKey: 'apptHours.advance',
    hintKey: 'apptHours.advanceMeans',
    min: 1,
    max: 3650,
  },
]

/**
 * Minutes from midnight as a clock time, for `<input type="time">` and for reading.
 *
 * Clamped below 24:00 because the input cannot hold it. A stored 1440 therefore shows as 23:59, and
 * `shape` below is what keeps it stored as 1440 until the closing time is actually changed.
 */
const text = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value)

const BLANK = (value: string): boolean => value.trim() === ''

/**
 * The form's own state. `opens` and `closes` are `HH:MM` for the time inputs and the four rules are
 * strings, so an unset shop can hold a genuinely empty field rather than a zero somebody has to
 * notice is a guess.
 */
interface Draft {
  readonly days: readonly Weekday[]
  readonly opens: string
  readonly closes: string
  readonly slotStep: string
  readonly buffer: string
  readonly notice: string
  readonly advance: string
}

const EMPTY: Draft = {
  days: [],
  opens: '',
  closes: '',
  slotStep: '',
  buffer: '',
  notice: '',
  advance: '',
}

const draftOf = (row: ScheduleRow | null): Draft =>
  row === null
    ? EMPTY
    : {
        days: [...row.openWeekdays].sort((a, b) => a - b) as Weekday[],
        opens: timeOf(row.opensAtMinute),
        closes: timeOf(row.closesAtMinute),
        slotStep: text(row.slotStepMinutes),
        buffer: text(row.bufferMinutes),
        notice: text(row.minNoticeMinutes),
        advance: text(row.maxAdvanceDays),
      }

export function AppointmentHours({
  canManage,
  branchId,
  branchName,
  loadSchedule,
  onWritten,
}: AppointmentHoursProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()

  const [row, setRow] = useState<ScheduleRow | null>(null)
  /** Separate from `row`, because `null` here is a real answer and not "still reading". */
  const [loaded, setLoaded] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  // An answer for a branch the shopkeeper has since left must not land on the new one.
  const generation = useRef(0)

  const refresh = useCallback(() => {
    generation.current += 1
    const mine = generation.current
    loadSchedule(branchId).then(
      (found) => {
        if (generation.current !== mine) return
        setRow(found)
        setDraft(draftOf(found))
        setLoaded(true)
        setLoadFailed(false)
      },
      () => {
        if (generation.current !== mine) return
        // Its own state: "these hours could not be read" is not "this branch has no hours".
        setLoaded(false)
        setLoadFailed(true)
      },
    )
  }, [branchId, loadSchedule])

  const path = `/branches/${encodeURIComponent(branchId)}/appointment-schedule`

  /**
   * A change to these hours this device has not got rid of yet, queued by an earlier mount or an
   * earlier session.
   *
   * Without this the second save is a real hazard rather than a nuisance: the first set omits
   * `expectedVersion`, so if it is still sitting in the queue the read still answers `null`, and a
   * second save would omit the version too and be refused `EXPECTED_VERSION_REQUIRED` once the
   * first one lands.
   */
  const stillWaiting = snapshot.writes.some(
    (write) => write.path === path && write.status !== 'sent',
  )

  useEffect(() => {
    // A role without the row is not asked: the read would answer 403 for nothing.
    if (canManage) refresh()
  }, [canManage, refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setError(null)
    setNotice(t('apptHours.saved'))
    // The re-read is not here. It is on the falling edge of `stillWaiting` below, which is the
    // same moment for this write and the right moment for the three this hook cannot see.
    onWritten?.()
  })

  // A write discarded from the blocked list never reaches `sent`, so without this the Save button
  // would stay disabled for good.
  useEffect(() => {
    if (writeId === null) return
    if (!snapshot.writes.some((candidate) => candidate.id === writeId)) setWriteId(null)
  }, [snapshot, writeId])

  /**
   * Read again the moment nothing of this screen's is waiting any more.
   *
   * `useWriteSettled` fires for this screen's own write and for nothing else, which left two ways
   * to end up offering a Save the server was certain to refuse:
   *
   * - **a first set queued by an earlier session lands while this screen is open.** Its id is not
   *   `writeId`, so nothing fired, `row` stayed `null`, and the next save omitted
   *   `expectedVersion` against a row that now exists: `EXPECTED_VERSION_REQUIRED`, at the head of
   *   the queue, which stops the till.
   * - **a write blocked on `VERSION_CONFLICT` is discarded in `BlockedWrites`.** Save came back
   *   carrying the version that had already lost, so the next save conflicted again, and the
   *   sentence telling the shopkeeper to read the hours again was the only thing on screen that
   *   could do it.
   *
   * The falling edge covers both, and it is also the right moment when two writes are queued at
   * once: reading after the first landed would show a row the second is about to change.
   */
  const wasWaiting = useRef(false)
  useEffect(() => {
    if (wasWaiting.current && !stillWaiting && canManage) refresh()
    wasWaiting.current = stillWaiting
  }, [stillWaiting, canManage, refresh])
  const busy = writeId !== null || stillWaiting

  const edit = (change: Partial<Draft>): void => {
    setDraft((was) => ({ ...was, ...change }))
    setError(null)
    setNotice(null)
  }

  const toggleDay = (day: Weekday): void => {
    edit({
      days: draft.days.includes(day)
        ? draft.days.filter((candidate) => candidate !== day)
        : [...draft.days, day].sort((a, b) => a - b),
    })
  }

  /** The refusal to show, or the body to send. Every check the server would make, in shop words. */
  const shape = (): { readonly error: string } | { readonly body: Record<string, unknown> } => {
    const opens = minuteOf(draft.opens)
    const closes = minuteOf(draft.closes)
    if (opens === null || closes === null) return { error: t('apptHours.timesRequired') }
    // 00:00 as a closing time is what somebody types for midnight, and minute 0 is not it. An
    // opening time of 00:00 is minute 0 and is a real answer, so neither is truthiness-checked.
    if (closes === 0) return { error: t('apptHours.closesMidnight') }
    if (closes <= opens) return { error: t('apptHours.timesBackwards') }

    if (draft.days.length === 0) return { error: t('apptHours.noDayTicked') }

    const missing = RULES.filter((rule) => BLANK(draft[rule.key])).map((rule) => t(rule.labelKey))
    if (missing.length > 0) return { error: t('apptHours.missing', { fields: missing.join(', ') }) }

    for (const rule of RULES) {
      const value = draft[rule.key].trim()
      if (!/^\d+$/.test(value) || Number(value) < rule.min || Number(value) > rule.max) {
        return {
          error: t('apptHours.notANumber', {
            field: t(rule.labelKey),
            min: rule.min,
            max: rule.max,
          }),
        }
      }
    }

    // A branch already trading to the end of the day keeps 1440 until its closing time is changed.
    const keepsMidnight =
      row !== null && row.closesAtMinute === DAY_MINUTES && draft.closes === timeOf(DAY_MINUTES)

    return {
      body: {
        openWeekdays: [...draft.days],
        opensAtMinute: opens,
        closesAtMinute: keepsMidnight ? DAY_MINUTES : closes,
        // Read from the draft rather than from an accumulator, after the loop above has proved
        // every one of the four is a whole number inside its bounds.
        slotStepMinutes: Number(draft.slotStep.trim()),
        bufferMinutes: Number(draft.buffer.trim()),
        minNoticeMinutes: Number(draft.notice.trim()),
        maxAdvanceDays: Number(draft.advance.trim()),
        // Absent on the first set, because there is nothing to have changed under it, and required
        // afterwards. Both the wrong way round are refusals that halt the queue.
        ...(row === null ? {} : { expectedVersion: row.version }),
      },
    }
  }

  const save = async (): Promise<void> => {
    const shaped = shape()
    if ('error' in shaped) {
      setError(shaped.error)
      return
    }
    setError(null)
    setNotice(null)
    const write = (await enqueue({
      method: 'PUT',
      path,
      label: t('apptHours.queueLabel', { branch: branchName }),
      body: shaped.body,
    })) as { id: string }
    setWriteId(write.id)
  }

  const timeField = (key: 'opens' | 'closes', labelKey: MessageKey): JSX.Element => {
    const id = `appt-hours-${key}`
    return (
      <div className="lm-field">
        {/* The label is its own element and the refusal lives outside it, at the foot of the form:
            an error inside the label makes the accessible name "label + message". */}
        <label className="lm-field__label" htmlFor={id}>
          {t(labelKey)}
        </label>
        <input
          id={id}
          className="lm-input"
          type="time"
          value={draft[key]}
          disabled={busy}
          onChange={(e) => {
            edit({ [key]: e.currentTarget.value })
          }}
        />
      </div>
    )
  }

  const line = statusLine(t, snapshot, writeId, t('queue.subject.appointmentHours'))
  const inFlight = writeId === null ? undefined : snapshot.writes.find((w) => w.id === writeId)
  const conflicted = inFlight?.status === 'blocked' && inFlight.error?.code === 'VERSION_CONFLICT'

  return (
    <section className="lm-screen" aria-labelledby="appt-hours-heading">
      <h1 id="appt-hours-heading" className="lm-screen__heading">
        {t('apptHours.heading')}
      </h1>

      <DataSurface
        state={
          !canManage
            ? { kind: 'denied' }
            : loadFailed
              ? {
                  kind: 'error',
                  message: t('apptHours.loadFailed', { branch: branchName }),
                  onRetry: refresh,
                }
              : !loaded
                ? { kind: 'loading' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={5}
        what={t('apptHours.what')}
        deniedMessage={t('apptHours.denied')}
      >
        <p className="lm-hint">{t('apptHours.means', { branch: branchName })}</p>
        {row === null ? (
          <p className="lm-hint" role="status">
            {t('apptHours.notSetYet', { branch: branchName })}
          </p>
        ) : null}

        <fieldset className="lm-fieldset">
          <legend className="lm-field__label">{t('apptHours.daysHeading')}</legend>
          <p className="lm-hint">{t('apptHours.daysMeans')}</p>
          <p className="lm-hint">{t('apptHours.daysOrder')}</p>
          {WEEKDAYS.map((weekday) => {
            const id = `appt-hours-day-${String(weekday.day)}`
            return (
              <div key={weekday.day} className="lm-field lm-field--inline">
                <input
                  id={id}
                  type="checkbox"
                  checked={draft.days.includes(weekday.day)}
                  disabled={busy}
                  onChange={() => {
                    toggleDay(weekday.day)
                  }}
                />
                <label className="lm-field__label" htmlFor={id}>
                  {t(weekday.key)}
                </label>
              </div>
            )
          })}
        </fieldset>

        <fieldset className="lm-fieldset">
          <legend className="lm-field__label">{t('apptHours.hoursHeading')}</legend>
          <p className="lm-hint">{t('apptHours.hoursMeans')}</p>
          {timeField('opens', 'apptHours.opensAt')}
          {timeField('closes', 'apptHours.closesAt')}
          <p className="lm-hint">{t('apptHours.midnightMeans')}</p>
          {/* `appointment_weekday_hours` is read by booking and written by nothing, so one day
              trading shorter hours than the rest cannot be said from the counter at all. Said in
              words, like the seasonal screen says what it cannot know, and it points at the one
              exception that can be set. */}
          <p className="lm-hint">{t('apptHours.sameEveryDay')}</p>
        </fieldset>

        <fieldset className="lm-fieldset">
          <legend className="lm-field__label">{t('apptHours.rulesHeading')}</legend>
          {RULES.map((rule) => {
            const id = `appt-hours-${rule.key}`
            return (
              <div key={rule.key} className="lm-field">
                <label className="lm-field__label" htmlFor={id}>
                  {t(rule.labelKey)}
                </label>
                <input
                  id={id}
                  className="lm-input"
                  type="text"
                  inputMode="numeric"
                  dir="ltr"
                  autoComplete="off"
                  aria-describedby={`${id}-hint`}
                  value={draft[rule.key]}
                  disabled={busy}
                  onChange={(e) => {
                    edit({ [rule.key]: e.currentTarget.value })
                  }}
                />
                <p id={`${id}-hint`} className="lm-hint">
                  {t(rule.hintKey)}
                </p>
              </div>
            )
          })}
          {/* Neither of the last two is enforced at the counter: `bookingRefusals` and
              `availableSlots` are called with no `now` anywhere in `apps/api`, which is what makes
              both rules inert. Said rather than implied, because a shopkeeper who sets a two-hour
              notice and then books somebody in for 20 minutes' time would otherwise read the
              screen as broken. */}
          <p className="lm-hint">{t('apptHours.policyInert')}</p>
        </fieldset>

        {error === null ? null : (
          <p className="lm-error" role="alert">
            {error}
          </p>
        )}
        {stillWaiting && writeId === null ? (
          <p className="lm-hint" role="status">
            {t('apptHours.stillWaiting')}
          </p>
        ) : null}
        {conflicted ? (
          <p className="lm-hint" role="alert">
            {t('apptHours.changedElsewhere')}
          </p>
        ) : null}

        <button
          type="button"
          className="lm-button lm-button--primary"
          disabled={busy}
          onClick={() => {
            void save()
          }}
        >
          {t('apptHours.save')}
        </button>

        {notice === null ? null : (
          <p className="lm-hint" role="status">
            {notice}
          </p>
        )}
        {/* No second reading of what is stored beside the form. The form is that reading, and a
            branch stored at 1440 would have it printed as 23:59, which is the one number on this
            screen that is not what the server holds. */}
        <p className="lm-hint">{line}</p>
      </DataSurface>
    </section>
  )
}
